import { computed, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import {
  TaskViewGroupChange,
  TaskViewSettingChange,
  TaskViewSettingValues
} from '@app/components/task/task-view-settings/task-view-settings.component';

export const TASK_DETAILS_VIEW_FIELDS = [
  'status',
  'progress',
  'priority',
  'location',
  'startDate',
  'endDate',
  'industry',
  'totalHours',
  'users',
  'tags',
  'estimationTime',
  'estimationCost',
  'estimationQuantity',
  'acknowledgement'
] as const;
export type TaskDetailsViewField = (typeof TASK_DETAILS_VIEW_FIELDS)[number];

interface TaskDetailsTaskDataVisibility {
  readonly status: boolean;
  readonly progress: boolean;
  readonly priority: boolean;
}

interface TaskDetailsEstimationVisibility {
  readonly time: boolean;
  readonly cost: boolean;
  readonly quantity: boolean;
}

const DEFAULT_TASK_DATA_VISIBILITY: TaskDetailsTaskDataVisibility = {
  status: true,
  progress: true,
  priority: true
};

const DEFAULT_ESTIMATION_VISIBILITY: TaskDetailsEstimationVisibility = {
  time: true,
  cost: true,
  quantity: true
};

/**
 * TaskDetailsViewSettingsService - Owns mobile detail-field visibility independently of the list.
 *
 * Scope: Splitview-level (`providers` on TaskSplitviewListComponent), retained when
 * the mobile and desktop detail branches are remounted at the responsive breakpoint.
 *
 * Usage: {@link TaskDetailsViewSettingsComponent} writes changes;
 * {@link TaskDetailsMetadataPanelComponent} reads visibility.
 */
@Injectable()
export class TaskDetailsViewSettingsService {
  private readonly lastTaskData: WritableSignal<TaskDetailsTaskDataVisibility> = signal(DEFAULT_TASK_DATA_VISIBILITY);
  private readonly lastLocationVisible: WritableSignal<boolean> = signal(true);
  private readonly lastIndustryVisible: WritableSignal<boolean> = signal(true);
  private readonly lastStartDateVisible: WritableSignal<boolean> = signal(true);
  private readonly lastEndDateVisible: WritableSignal<boolean> = signal(true);
  private readonly lastEstimation: WritableSignal<TaskDetailsEstimationVisibility> =
    signal(DEFAULT_ESTIMATION_VISIBILITY);

  public readonly statusVisible: WritableSignal<boolean> = signal(true);
  public readonly progressVisible: WritableSignal<boolean> = signal(true);
  public readonly priorityVisible: WritableSignal<boolean> = signal(true);
  public readonly locationVisible: WritableSignal<boolean> = signal(false);
  public readonly startDateVisible: WritableSignal<boolean> = signal(false);
  public readonly endDateVisible: WritableSignal<boolean> = signal(false);
  public readonly industryVisible: WritableSignal<boolean> = signal(false);
  public readonly totalHoursVisible: WritableSignal<boolean> = signal(false);
  public readonly usersVisible: WritableSignal<boolean> = signal(false);
  public readonly tagsVisible: WritableSignal<boolean> = signal(false);
  public readonly estimationTimeVisible: WritableSignal<boolean> = signal(false);
  public readonly estimationCostVisible: WritableSignal<boolean> = signal(false);
  public readonly estimationQuantityVisible: WritableSignal<boolean> = signal(false);
  public readonly acknowledgementVisible: WritableSignal<boolean> = signal(false);

  public readonly taskDataVisible: Signal<boolean> = computed(
    () => this.statusVisible() || this.progressVisible() || this.priorityVisible()
  );

  public readonly estimationVisible: Signal<boolean> = computed(
    () => this.estimationTimeVisible() || this.estimationCostVisible() || this.estimationQuantityVisible()
  );

  public readonly values: Signal<TaskViewSettingValues> = computed(() => ({
    status: this.statusVisible(),
    progress: this.progressVisible(),
    priority: this.priorityVisible(),
    location: this.locationVisible(),
    startDate: this.startDateVisible(),
    endDate: this.endDateVisible(),
    industry: this.industryVisible(),
    totalHours: this.totalHoursVisible(),
    users: this.usersVisible(),
    tags: this.tagsVisible(),
    estimationTime: this.estimationTimeVisible(),
    estimationCost: this.estimationCostVisible(),
    estimationQuantity: this.estimationQuantityVisible(),
    acknowledgement: this.acknowledgementVisible()
  }));

  public readonly groupEnabled: Signal<Readonly<Record<string, boolean>>> = computed(() => ({
    'task-data': this.taskDataVisible(),
    location: this.locationVisible() || this.industryVisible(),
    totalHours: this.totalHoursVisible(),
    dates: this.startDateVisible() || this.endDateVisible(),
    estimation: this.estimationVisible(),
    users: this.usersVisible(),
    tags: this.tagsVisible(),
    acknowledgement: this.acknowledgementVisible()
  }));

  public applyChange(change: TaskViewSettingChange): void {
    if (typeof change.value !== 'boolean') {
      return;
    }

    switch (change.key) {
      case 'status':
        this.applyTaskDataPart('status', change.value);
        break;
      case 'progress':
        this.applyTaskDataPart('progress', change.value);
        break;
      case 'priority':
        this.applyTaskDataPart('priority', change.value);
        break;
      case 'location':
        this.rememberLocation(change.value, this.industryVisible());
        this.locationVisible.set(change.value);
        break;
      case 'industry':
        this.rememberLocation(this.locationVisible(), change.value);
        this.industryVisible.set(change.value);
        break;
      case 'startDate':
        this.rememberDates(change.value, this.endDateVisible());
        this.startDateVisible.set(change.value);
        break;
      case 'endDate':
        this.rememberDates(this.startDateVisible(), change.value);
        this.endDateVisible.set(change.value);
        break;
      case 'totalHours':
        this.totalHoursVisible.set(change.value);
        break;
      case 'users':
        this.usersVisible.set(change.value);
        break;
      case 'estimationTime':
        this.applyEstimationPart('time', change.value);
        break;
      case 'estimationCost':
        this.applyEstimationPart('cost', change.value);
        break;
      case 'estimationQuantity':
        this.applyEstimationPart('quantity', change.value);
        break;
      case 'tags':
        this.tagsVisible.set(change.value);
        break;
      case 'acknowledgement':
        this.acknowledgementVisible.set(change.value);
        break;
    }
  }

  public applyGroup(change: TaskViewGroupChange): void {
    switch (change.key) {
      case 'all':
        this.applyAll(change.enabled);
        break;
      case 'task-data': {
        if (!change.enabled) {
          this.rememberTaskData({
            status: this.statusVisible(),
            progress: this.progressVisible(),
            priority: this.priorityVisible()
          });
        }
        const restoredTaskData = this.lastTaskData();
        this.statusVisible.set(change.enabled ? restoredTaskData.status : false);
        this.progressVisible.set(change.enabled ? restoredTaskData.progress : false);
        this.priorityVisible.set(change.enabled ? restoredTaskData.priority : false);
        break;
      }
      case 'location':
        if (!change.enabled) {
          this.rememberLocation(this.locationVisible(), this.industryVisible());
        }
        this.locationVisible.set(change.enabled ? this.lastLocationVisible() : false);
        this.industryVisible.set(change.enabled ? this.lastIndustryVisible() : false);
        break;
      case 'dates':
        if (!change.enabled) {
          this.rememberDates(this.startDateVisible(), this.endDateVisible());
        }
        this.startDateVisible.set(change.enabled ? this.lastStartDateVisible() : false);
        this.endDateVisible.set(change.enabled ? this.lastEndDateVisible() : false);
        break;
      case 'totalHours':
        this.totalHoursVisible.set(change.enabled);
        break;
      case 'estimation': {
        const current: TaskDetailsEstimationVisibility = {
          time: this.estimationTimeVisible(),
          cost: this.estimationCostVisible(),
          quantity: this.estimationQuantityVisible()
        };
        if (!change.enabled && (current.time || current.cost || current.quantity)) {
          this.lastEstimation.set(current);
        }
        const restored = this.lastEstimation();
        this.estimationTimeVisible.set(change.enabled ? restored.time : false);
        this.estimationCostVisible.set(change.enabled ? restored.cost : false);
        this.estimationQuantityVisible.set(change.enabled ? restored.quantity : false);
        break;
      }
      case 'users':
        this.usersVisible.set(change.enabled);
        break;
      case 'tags':
        this.tagsVisible.set(change.enabled);
        break;
      case 'acknowledgement':
        this.acknowledgementVisible.set(change.enabled);
        break;
    }
  }

  private applyAll(enabled: boolean): void {
    if (!enabled) {
      this.rememberTaskData({
        status: this.statusVisible(),
        progress: this.progressVisible(),
        priority: this.priorityVisible()
      });
      this.rememberLocation(this.locationVisible(), this.industryVisible());
      this.rememberDates(this.startDateVisible(), this.endDateVisible());
      this.rememberEstimation({
        time: this.estimationTimeVisible(),
        cost: this.estimationCostVisible(),
        quantity: this.estimationQuantityVisible()
      });
    }

    this.statusVisible.set(enabled);
    this.progressVisible.set(enabled);
    this.priorityVisible.set(enabled);
    this.locationVisible.set(enabled);
    this.industryVisible.set(enabled);
    this.totalHoursVisible.set(enabled);
    this.startDateVisible.set(enabled);
    this.endDateVisible.set(enabled);
    this.estimationTimeVisible.set(enabled);
    this.estimationCostVisible.set(enabled);
    this.estimationQuantityVisible.set(enabled);
    this.usersVisible.set(enabled);
    this.tagsVisible.set(enabled);
    this.acknowledgementVisible.set(enabled);
  }

  private applyTaskDataPart(part: keyof TaskDetailsTaskDataVisibility, visible: boolean): void {
    const next: TaskDetailsTaskDataVisibility = {
      status: part === 'status' ? visible : this.statusVisible(),
      progress: part === 'progress' ? visible : this.progressVisible(),
      priority: part === 'priority' ? visible : this.priorityVisible()
    };
    this.rememberTaskData(next);
    this.statusVisible.set(next.status);
    this.progressVisible.set(next.progress);
    this.priorityVisible.set(next.priority);
  }

  private applyEstimationPart(part: keyof TaskDetailsEstimationVisibility, visible: boolean): void {
    const next: TaskDetailsEstimationVisibility = {
      time: part === 'time' ? visible : this.estimationTimeVisible(),
      cost: part === 'cost' ? visible : this.estimationCostVisible(),
      quantity: part === 'quantity' ? visible : this.estimationQuantityVisible()
    };
    this.rememberEstimation(next);
    this.estimationTimeVisible.set(next.time);
    this.estimationCostVisible.set(next.cost);
    this.estimationQuantityVisible.set(next.quantity);
  }

  private rememberTaskData(visibility: TaskDetailsTaskDataVisibility): void {
    if (!visibility.status && !visibility.progress && !visibility.priority) {
      return;
    }

    this.lastTaskData.set(visibility);
  }

  private rememberLocation(locationVisible: boolean, industryVisible: boolean): void {
    if (!locationVisible && !industryVisible) {
      return;
    }

    this.lastLocationVisible.set(locationVisible);
    this.lastIndustryVisible.set(industryVisible);
  }

  private rememberDates(startDateVisible: boolean, endDateVisible: boolean): void {
    if (!startDateVisible && !endDateVisible) {
      return;
    }

    this.lastStartDateVisible.set(startDateVisible);
    this.lastEndDateVisible.set(endDateVisible);
  }

  private rememberEstimation(visibility: TaskDetailsEstimationVisibility): void {
    if (!visibility.time && !visibility.cost && !visibility.quantity) {
      return;
    }

    this.lastEstimation.set(visibility);
  }
}
